import { useHeaderHeight } from '@react-navigation/elements'
import { useLocalSearchParams, useNavigation } from 'expo-router'
import { useEffect, useMemo } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { useLiveMonitor } from '@/hooks/monitor/useLiveMonitor'
import { pbDate } from '@/lib/dates'
import { useTrailSettings } from '@/stores/monitor-store'
import type { UserLocationWithStatus } from '@/types/movement'
import { UserTrailWindowChips } from '@/ui/monitor/UserTrailWindowChips'
import { MonitorMapView } from '@/ui/monitor/views/MapView'

function filterPathByTrailWindow(
    user: UserLocationWithStatus,
    trailWindow: number | null,
    now: Date
): UserLocationWithStatus {
    if (trailWindow === null) return user
    // PocketBase stores timestamps as "YYYY-MM-DD HH:MM:SS.sssZ" (space, not T),
    // so we must match that format for lexicographic comparison against
    // PathPoint.timestamp to work.
    const cutoff = pbDate(new Date(now.getTime() - trailWindow * 60 * 1000))
    return {
        ...user,
        path: user.path.filter(p => p.timestamp >= cutoff),
    }
}

export default function MonitorUserDetailPage() {
    const theme = useTheme()
    const navigation = useNavigation()
    const { userId } = useLocalSearchParams<{ userId: string }>()
    const headerHeight = useHeaderHeight()
    const paddingTop = Platform.OS === 'ios' ? headerHeight : 0

    const data = useLiveMonitor()
    const { trailWindow, setTrailWindow } = useTrailSettings()

    // Default to a 30-minute trail when landing on the detail page so managers
    // see recent activity rather than the full day crammed into the viewport.
    // setTrailWindow is a stable Zustand setter, so this still effectively
    // runs only on mount.
    useEffect(() => {
        setTrailWindow(30)
    }, [setTrailWindow])

    const rawUser = useMemo(
        () => data.userLocations.find(u => u.userId === userId),
        [data.userLocations, userId]
    )

    const filteredUser = useMemo(
        () => (rawUser ? filterPathByTrailWindow(rawUser, trailWindow, data.now) : undefined),
        [rawUser, trailWindow, data.now]
    )

    // biome-ignore lint/correctness/useExhaustiveDependencies: navigation is stable; title depends on user name only
    useEffect(() => {
        if (rawUser) {
            navigation.setOptions({ title: rawUser.userName })
        }
    }, [rawUser?.userName])

    if (!rawUser || !filteredUser) {
        return (
            <View
                style={[styles.container, { backgroundColor: theme.colors.background, paddingTop }]}
            >
                <View style={styles.empty}>
                    <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                        {data.isPending ? 'Loading…' : 'User not found'}
                    </Text>
                </View>
            </View>
        )
    }

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background, paddingTop }]}>
            <View style={styles.mapContainer}>
                <MonitorMapView
                    userLocations={[{ ...filteredUser, visible: true }]}
                    locationCircles={data.locationCircles}
                    selectedLocation={data.selectedLocation}
                    selectedLocationActivity={data.selectedLocationActivity}
                    isGeofenceDataPending={data.isGeofenceDataPending}
                    userColorMap={data.userColorMap}
                    isPending={data.isPending}
                />
            </View>
            <UserTrailWindowChips />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    mapContainer: {
        flex: 1,
    },
    empty: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
})
